<template>
  <Teleport v-if="toolbarTarget" :to="toolbarTarget">
    <div class="hs-menu">
      <button
        class="hs-btn"
        :class="{ 'hs-btn--active': visible }"
        @click="toggleVisible"
        title="Atalho [H] para alternar o Hover Sim"
      >
        <span class="shortcut">[H]</span>
        Hover Sim: <strong>{{ visible ? 'ON' : 'OFF' }}</strong>
      </button>

      <div class="hs-menu__panel">
        <div
          class="hs-field"
          title="Tamanho do lote simulado ao passar o mouse no gráfico"
        >
          <label class="hs-field__lbl">Lote:</label>
          <input
            type="number"
            v-model.number="lotQty"
            step="0.01"
            min="0.001"
            max="5"
            class="hs-field__input hs-field__input--qty"
          />
          <span class="hs-field__unit">BTC</span>
        </div>

        <div
          class="hs-field"
          title="Primeira banda de preço a partir da linha do mouse"
        >
          <label class="hs-field__lbl">Perto:</label>
          <input
            type="number"
            v-model.number="nearPct"
            step="0.1"
            min="0.1"
            max="20"
            class="hs-field__input"
          />
          <span class="hs-field__unit">%</span>
        </div>

        <div
          class="hs-field"
          title="Segunda banda de preço a partir da linha do mouse"
        >
          <label class="hs-field__lbl">Longe:</label>
          <input
            type="number"
            v-model.number="farPct"
            step="0.1"
            min="0.1"
            max="20"
            class="hs-field__input"
          />
          <span class="hs-field__unit">%</span>
        </div>

        <div
          class="hs-field hs-field--target"
          title="Margem de target da nova posição (mesmo valor do Inventory Profile em Target Mode)"
        >
          <label class="hs-field__lbl">Margem:</label>
          <input
            type="number"
            v-model.number="marginInputPct"
            step="0.1"
            min="0.1"
            max="5.0"
            class="hs-field__input"
          />
          <span class="hs-field__unit">%</span>
        </div>
      </div>
    </div>
  </Teleport>

  <div v-if="visible && ready && preview" class="hover-sim__overlay">
    <div
      v-if="entryY != null"
      class="hs-action"
      :class="preview.action === 'buy' ? 'hs-action--buy' : 'hs-action--sell'"
      :style="actionStyle"
    >
      {{ preview.action === 'buy' ? 'BUY' : 'SELL' }} {{ formatBtc(preview.qty) }}
    </div>
    <div
      v-for="band in renderedBands"
      :key="band.key"
      class="hs-band"
      :class="[
        preview.action === 'buy' ? 'hs-band--buy' : 'hs-band--sell',
        band.key === 'near' ? 'hs-band--near' : 'hs-band--far',
      ]"
      :style="{ top: band.top + 'px', height: band.height + 'px' }"
    >
      <div class="hs-band__meta">
        <span class="hs-band__pct">{{ preview.action === 'buy' ? '-' : '+' }}{{ band.pct }}%</span>
        <span class="hs-band__sep">|</span>
        <span class="hs-band__price">${{ formatUsd(band.markPrice) }}</span>
      </div>
      <div class="hs-band__pnl" :class="band.netPnl >= 0 ? 'is-pos' : 'is-neg'">
        {{ formatPnl(band.netPnl) }}
      </div>
      <div class="hs-band__compare">
        vs {{ formatPnl(band.baselinePnl) }}
        <span class="hs-band__delta" :class="band.deltaPnl >= 0 ? 'is-pos' : 'is-neg'">
          Δ {{ formatPnl(band.deltaPnl) }}
        </span>
      </div>
    </div>
    <div
      v-if="renderedTarget"
      class="hs-band hs-band--target"
      :style="{ top: renderedTarget.top + 'px', height: renderedTarget.height + 'px' }"
    >
      <div class="hs-band__meta">
        <span class="hs-band__tag">T</span>
        <span class="hs-band__pct">{{ renderedTarget.direction === 'up' ? '+' : '-' }}{{ Number(renderedTarget.pct).toFixed(1) }}%</span>
        <span class="hs-band__sep">|</span>
        <span class="hs-band__price">${{ formatUsd(renderedTarget.markPrice) }}</span>
      </div>
      <div class="hs-band__pnl" :class="renderedTarget.netPnl >= 0 ? 'is-pos' : 'is-neg'">
        {{ formatPnl(renderedTarget.netPnl) }}
      </div>
      <div class="hs-band__compare">
        lote {{ formatPnl(renderedTarget.newPositionPnl) }}
        <span class="hs-band__delta" :class="renderedTarget.inventoryPnl >= 0 ? 'is-pos' : 'is-neg'">
          inv {{ formatPnl(renderedTarget.inventoryPnl) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useHoverSim } from './useHoverSim'

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  priceToY: { type: Function, required: true },
  hoverPrice: { type: Number, default: null },
  currentPrice: { type: Number, default: 0 },
  ready: { type: Boolean, default: false },
  toolbarTarget: { type: [Object, String], default: null },
})

const renderedBands = ref([])
const renderedTarget = ref(null)
const entryY = ref(null)

const {
  visible,
  lotQty,
  nearPct,
  farPct,
  marginInputPct,
  toggleVisible,
  previewAt,
} = useHoverSim()

const preview = computed(() => {
  if (!visible.value || !props.ready) return null
  return previewAt(props.hoverPrice, props.currentPrice)
})

const actionStyle = computed(() => {
  if (entryY.value == null || !preview.value) return {}
  return { top: entryY.value + 'px', transform: 'translateY(-50%)' }
})

function formatBtc(value) {
  return Number(value || 0).toFixed(2)
}

function formatUsd(value) {
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

function formatPnl(value) {
  const n = Number(value) || 0
  const sign = n > 0 ? '+' : ''
  return `${sign}$${n.toFixed(0)}`
}

function bandBox(y0, y1) {
  if (y0 == null || y1 == null) return null
  const height = Math.abs(y1 - y0)
  if (height < 2) return null
  return {
    top: Math.min(y0, y1),
    height,
  }
}

function updateBandCoordinates() {
  const sim = preview.value
  if (!sim || typeof props.priceToY !== 'function') {
    renderedBands.value = []
    renderedTarget.value = null
    entryY.value = null
    return
  }

  const yEntry = props.priceToY(sim.entryPrice)
  entryY.value = yEntry != null ? yEntry : null
  const boxes = []
  let yPrev = yEntry

  for (const band of sim.bands) {
    const yMark = props.priceToY(band.markPrice)
    const box = bandBox(yPrev, yMark)
    if (box) {
      boxes.push({
        ...band,
        ...box,
      })
    }
    yPrev = yMark
  }

  renderedBands.value = boxes

  const target = sim.targetBand
  if (!target) {
    renderedTarget.value = null
    return
  }
  const yTarget = props.priceToY(target.markPrice)
  const targetBox = bandBox(yEntry, yTarget)
  renderedTarget.value = targetBox ? { ...target, ...targetBox } : null
}

let rafId = null
function startSyncLoop() {
  function loop() {
    if (visible.value && props.ready) {
      updateBandCoordinates()
    }
    rafId = requestAnimationFrame(loop)
  }
  loop()
}

function stopSyncLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch(preview, updateBandCoordinates)

onMounted(() => {
  startSyncLoop()
})

onUnmounted(() => {
  stopSyncLoop()
})
</script>

<style scoped>
.hs-menu {
  position: relative;
}

.hs-menu__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 180px;
  padding: 10px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  flex-direction: column;
  gap: 8px;
}

.hs-menu__panel::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.hs-menu:hover .hs-menu__panel,
.hs-menu:focus-within .hs-menu__panel {
  display: flex;
}

.hs-field {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #21262d;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 3px 8px;
}

.hs-field__lbl {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.hs-field__input {
  width: 44px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #58a6ff;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  padding: 2px 0;
}

.hs-field__input--qty {
  width: 56px;
}

.hs-field--target {
  border-color: rgba(227, 179, 65, 0.45);
}

.hs-field__input:focus {
  outline: none;
  border-color: #58a6ff;
}

.hs-field--target .hs-field__input {
  color: #e3b341;
}

.hs-field--target .hs-field__input:focus {
  border-color: #e3b341;
}

.hs-field__unit {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.hs-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.hs-btn:hover {
  background: #30363d;
  border-color: #8b949e;
}

.hs-btn--active {
  background: rgba(88, 166, 255, 0.15);
  color: #58a6ff;
  border-color: #388bfd;
}

.shortcut {
  color: #8b949e;
  font-weight: 400;
  font-size: 11px;
}

.hover-sim__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
}

.hs-band {
  position: absolute;
  left: 0;
  width: 20%;
  box-sizing: border-box;
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  border-left: 3px solid;
}

.hs-band--buy.hs-band--near {
  background: rgba(46, 160, 67, 0.20);
  border-left-color: #2ea043;
}

.hs-band--buy.hs-band--far {
  background: rgba(88, 196, 110, 0.38);
  border-left-color: #56d364;
}

.hs-band--sell.hs-band--near {
  background: rgba(248, 81, 73, 0.20);
  border-left-color: #da3633;
}

.hs-band--sell.hs-band--far {
  background: rgba(255, 130, 105, 0.38);
  border-left-color: #ff7b72;
}

.hs-band--target {
  background: rgba(227, 179, 65, 0.20);
  border-left-color: #e3b341;
  overflow: visible;
  z-index: 9;
}

.hs-band__tag {
  background: rgba(227, 179, 65, 0.25);
  color: #e3b341;
  font-size: 9px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid #e3b341;
}

.hs-band__meta {
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 10px;
  font-weight: 700;
  color: #f0f6fc;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

.hs-action {
  position: absolute;
  left: 20%;
  width: auto;
  box-sizing: border-box;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.hs-action--buy {
  color: #3fb950;
}

.hs-action--sell {
  color: #f85149;
}

.hs-band__pct,
.hs-band__price {
  color: #c9d1d9;
  font-weight: 600;
}

.hs-band__sep {
  color: #8b949e;
  font-weight: 600;
}

.hs-band__pnl {
  font-size: 15px;
  font-weight: 800;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.hs-band__compare {
  font-size: 10px;
  font-weight: 600;
  color: #c9d1d9;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

.hs-band__delta {
  margin-left: 6px;
  font-weight: 800;
}

.hs-band__pnl.is-pos,
.hs-band__delta.is-pos {
  color: #3fb950;
}

.hs-band__pnl.is-neg,
.hs-band__delta.is-neg {
  color: #f85149;
}
</style>
